import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Users, Upload, Loader2 } from 'lucide-react';

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'internship', label: 'Internship' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'Onsite' },
];

const DOCUMENT_SLOTS = [
  { key: 'job_description', label: 'Job Description (PDF/DOCX)' },
  { key: 'company_profile', label: 'Company Profile' },
  { key: 'recruitment_documents', label: 'Recruitment Documents' },
  { key: 'other', label: 'Other Supporting Documents' },
];

const emptyForm = {
  // Company information
  contact_person: '', contact_position: '', contact_email: '', contact_phone: '',
  company_website: '', country: '', state_region: '', company_address: '',
  industry: '', company_size: '',
  // Recruitment requirements
  job_title: '', positions_count: 1, employment_type: 'full_time', location: '',
  department: '', job_description: '', responsibilities: '', required_qualifications: '',
  years_experience: '', preferred_certifications: '', salary_range: '', benefits_offered: '',
  preferred_start_date: '', recruitment_deadline: '', urgency: 'normal',
  // Candidate preferences
  gender_preference: '', age_range: '', nationality_preference: '',
  work_authorization_requirements: '', driving_license_required: false, travel_required: false,
  // Additional details
  interview_stages_count: '', assessment_required: false, background_check_required: false,
  medical_check_required: false, additional_notes: '',
};

export default function DirectRecruitmentPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const recruitmentCredits = user?.service_credits?.direct_recruitment || 0;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [requirements, setRequirements] = useState([]);
  const [newRequirement, setNewRequirement] = useState('');
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [languages, setLanguages] = useState([]);
  const [newLanguage, setNewLanguage] = useState('');
  const [documents, setDocuments] = useState({});
  const [pricingAcknowledged, setPricingAcknowledged] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const addToList = (value, setter, list) => {
    const v = value.trim();
    if (!v) return;
    if (!list.includes(v)) setter(prev => [...prev, v]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.job_title || !formData.job_description || !formData.location) {
      toast.error('Please fill in job title, description and location');
      return;
    }
    if (!pricingAcknowledged) {
      toast.error('You must acknowledge the 15% placement fee policy before submitting');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        positions_count: parseInt(formData.positions_count || 1, 10),
        interview_stages_count: formData.interview_stages_count ? parseInt(formData.interview_stages_count, 10) : null,
        requirements,
        required_skills: requiredSkills,
        languages_required: languages,
        pricing_acknowledged: pricingAcknowledged,
      };

      const res = await api.post('/recruitment/requests', payload);
      const requestId = res.data.id;

      // Upload any attached documents against the newly created request
      const uploadEntries = Object.entries(documents).filter(([, file]) => file);
      for (const [category, file] of uploadEntries) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await api.post(`/recruitment/requests/${requestId}/documents?category=${category}`, fd);
        } catch {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      toast.success('Employment request submitted successfully');
      navigate('/employer/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit employment request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#003366]" />
              </div>
              <div>
                <CardTitle className="text-2xl">Submit Employment Request</CardTitle>
                <CardDescription>
                  Tell us about your customized recruitment needs and our team will follow up.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {recruitmentCredits > 0 ? (
              <div className="mb-6 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
                You have {recruitmentCredits} Direct Recruitment Service credit(s). This request will be submitted as a paid, priority engagement.
              </div>
            ) : (
              <div className="mb-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3">
                <span>This will be submitted as a free inquiry. Purchase a Direct Recruitment Service credit for priority handling.</span>
                <Button type="button" size="sm" variant="outline" className="border-amber-300 whitespace-nowrap" onClick={() => navigate('/services')}>
                  Buy a credit
                </Button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-10">

              {/* Company Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_position">Position</Label>
                    <Input id="contact_position" name="contact_position" value={formData.contact_position} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email</Label>
                    <Input id="contact_email" name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone Number</Label>
                    <Input id="contact_phone" name="contact_phone" value={formData.contact_phone} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_website">Company Website</Label>
                    <Input id="company_website" name="company_website" value={formData.company_website} onChange={handleChange} placeholder="https://..." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input id="industry" name="industry" value={formData.industry} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input id="country" name="country" value={formData.country} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state_region">State/Region</Label>
                    <Input id="state_region" name="state_region" value={formData.state_region} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_size">Company Size</Label>
                    <Input id="company_size" name="company_size" value={formData.company_size} onChange={handleChange} placeholder="e.g. 50-200 employees" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="company_address">Company Address</Label>
                    <Input id="company_address" name="company_address" value={formData.company_address} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* Recruitment Requirements */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Recruitment Requirements</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="job_title">Job Title *</Label>
                      <Input id="job_title" name="job_title" value={formData.job_title} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Job Location *</Label>
                      <Input id="location" name="location" value={formData.location} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="positions_count">Number of Employees Needed</Label>
                      <Input id="positions_count" name="positions_count" type="number" min={1} value={formData.positions_count} onChange={handleChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employment_type">Employment Type</Label>
                      <Select value={formData.employment_type} onValueChange={(v) => setFormData(prev => ({ ...prev, employment_type: v }))}>
                        <SelectTrigger id="employment_type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="department">Department</Label>
                      <Input id="department" name="department" value={formData.department} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job_description">Job Description *</Label>
                    <Textarea id="job_description" name="job_description" value={formData.job_description} onChange={handleChange} rows={5} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="responsibilities">Responsibilities</Label>
                    <Textarea id="responsibilities" name="responsibilities" value={formData.responsibilities} onChange={handleChange} rows={4} />
                  </div>

                  <div className="space-y-2">
                    <Label>Required Skills</Label>
                    <div className="flex gap-2">
                      <Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="e.g. Excel, Project Management" />
                      <Button type="button" variant="outline" onClick={() => { addToList(newSkill, setRequiredSkills, requiredSkills); setNewSkill(''); }}>Add</Button>
                    </div>
                    {requiredSkills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {requiredSkills.map((s, i) => (
                          <Badge key={i} variant="outline" className="cursor-pointer" onClick={() => setRequiredSkills(prev => prev.filter((_, idx) => idx !== i))}>{s} ×</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="required_qualifications">Required Qualifications</Label>
                      <Input id="required_qualifications" name="required_qualifications" value={formData.required_qualifications} onChange={handleChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="years_experience">Years of Experience</Label>
                      <Input id="years_experience" name="years_experience" value={formData.years_experience} onChange={handleChange} placeholder="e.g. 3-5 years" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred_certifications">Preferred Certifications</Label>
                      <Input id="preferred_certifications" name="preferred_certifications" value={formData.preferred_certifications} onChange={handleChange} />
                    </div>
                    <div className="space-y-2">
                      <Label>Languages Required</Label>
                      <div className="flex gap-2">
                        <Input value={newLanguage} onChange={(e) => setNewLanguage(e.target.value)} placeholder="e.g. English, French" />
                        <Button type="button" variant="outline" onClick={() => { addToList(newLanguage, setLanguages, languages); setNewLanguage(''); }}>Add</Button>
                      </div>
                      {languages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {languages.map((l, i) => (
                            <Badge key={i} variant="outline" className="cursor-pointer" onClick={() => setLanguages(prev => prev.filter((_, idx) => idx !== i))}>{l} ×</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="salary_range">Salary Range</Label>
                      <Input id="salary_range" name="salary_range" value={formData.salary_range} onChange={handleChange} placeholder="e.g. £60,000 - £75,000" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="benefits_offered">Benefits Offered</Label>
                      <Input id="benefits_offered" name="benefits_offered" value={formData.benefits_offered} onChange={handleChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred_start_date">Preferred Start Date</Label>
                      <Input id="preferred_start_date" name="preferred_start_date" type="date" value={formData.preferred_start_date} onChange={handleChange} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="recruitment_deadline">Recruitment Deadline</Label>
                      <Input id="recruitment_deadline" name="recruitment_deadline" type="date" value={formData.recruitment_deadline} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="urgency">Urgency / Priority</Label>
                    <Select value={formData.urgency} onValueChange={(value) => setFormData(prev => ({ ...prev, urgency: value }))}>
                      <SelectTrigger id="urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Key Requirements (general tags)</Label>
                    <div className="flex gap-2">
                      <Input value={newRequirement} onChange={(e) => setNewRequirement(e.target.value)} placeholder="e.g. 5+ years experience, CIPD qualified" />
                      <Button type="button" onClick={() => { addToList(newRequirement, setRequirements, requirements); setNewRequirement(''); }} variant="outline">Add</Button>
                    </div>
                    {requirements.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {requirements.map((req, idx) => (
                          <Badge key={idx} variant="outline" className="cursor-pointer" onClick={() => setRequirements(prev => prev.filter((_, i) => i !== idx))}>{req} ×</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Candidate Preferences */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Candidate Preferences <span className="text-sm font-normal text-muted-foreground">(all optional)</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gender_preference">Gender Preference</Label>
                    <Input id="gender_preference" name="gender_preference" value={formData.gender_preference} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age_range">Age Range</Label>
                    <Input id="age_range" name="age_range" value={formData.age_range} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nationality_preference">Nationality Preference</Label>
                    <Input id="nationality_preference" name="nationality_preference" value={formData.nationality_preference} onChange={handleChange} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="work_authorization_requirements">Work Authorization Requirements</Label>
                    <Input id="work_authorization_requirements" name="work_authorization_requirements" value={formData.work_authorization_requirements} onChange={handleChange} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="driving_license_required" checked={formData.driving_license_required} onChange={handleChange} className="w-4 h-4" />
                    Driving License Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="travel_required" checked={formData.travel_required} onChange={handleChange} className="w-4 h-4" />
                    Travel Required
                  </label>
                </div>
              </div>

              {/* Additional Recruitment Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Additional Recruitment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="interview_stages_count">Number of Interview Stages</Label>
                    <Input id="interview_stages_count" name="interview_stages_count" type="number" min={0} value={formData.interview_stages_count} onChange={handleChange} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="assessment_required" checked={formData.assessment_required} onChange={handleChange} className="w-4 h-4" />
                    Assessment Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="background_check_required" checked={formData.background_check_required} onChange={handleChange} className="w-4 h-4" />
                    Background Check Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="medical_check_required" checked={formData.medical_check_required} onChange={handleChange} className="w-4 h-4" />
                    Medical Check Required
                  </label>
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="additional_notes">Additional Notes</Label>
                  <Textarea id="additional_notes" name="additional_notes" value={formData.additional_notes} onChange={handleChange} rows={3} />
                </div>
              </div>

              {/* File Uploads */}
              <div>
                <h3 className="text-lg font-semibold mb-4">File Uploads</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {DOCUMENT_SLOTS.map((slot) => (
                    <div key={slot.key} className="space-y-2">
                      <Label>{slot.label}</Label>
                      <label className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                        <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="truncate text-muted-foreground">
                          {documents[slot.key]?.name || 'Choose file...'}
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                          onChange={(e) => setDocuments(prev => ({ ...prev, [slot.key]: e.target.files?.[0] || null }))}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Notice */}
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 space-y-3">
                <p className="font-semibold text-blue-900">Recruitment Service Fee</p>
                <p className="text-sm text-blue-800">
                  We charge <strong>15% of the first annual income/salary of every employee successfully supplied</strong> through our Direct Employment Solutions service.
                </p>
                <label className="flex items-start gap-2 text-sm text-blue-900">
                  <input
                    type="checkbox"
                    checked={pricingAcknowledged}
                    onChange={(e) => setPricingAcknowledged(e.target.checked)}
                    className="w-4 h-4 mt-0.5"
                    required
                  />
                  <span>I understand and agree that WorksRun charges 15% of the first annual income of every employee successfully placed.</span>
                </label>
              </div>

              <div className="flex justify-end">
                <Button type="submit" className="bg-[#003366] hover:bg-[#003366]/90" disabled={loading || !pricingAcknowledged}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : 'Submit Employment Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
